import React, { useState, useEffect } from 'react';
import { host_url } from '../../App';
import './Tagesablauf.css';

// Zeitachse wie auf dem gedruckten Tagesablaufblatt: 7 bis 24 Uhr
const START_STUNDE = 7;
const ENDE_STUNDE = 24;
const GESAMT_MINUTEN = (ENDE_STUNDE - START_STUNDE) * 60;
const STUNDEN = Array.from({ length: ENDE_STUNDE - START_STUNDE + 1 }, (_, i) => START_STUNDE + i);

const WOCHENTAGE_KURZ = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const WOCHENTAGE_LANG = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

function zeitZuProzent(zeit) {
  const [h, m] = String(zeit).split(':').map(Number);
  const minuten = h * 60 + (m || 0) - START_STUNDE * 60;
  return Math.min(100, Math.max(0, (minuten / GESAMT_MINUTEN) * 100));
}

function heutigesDatum() {
  const heute = new Date();
  const monat = String(heute.getMonth() + 1).padStart(2, '0');
  const tag = String(heute.getDate()).padStart(2, '0');
  return `${heute.getFullYear()}-${monat}-${tag}`;
}

function tagLabel(datum, lang = false) {
  const d = new Date(`${datum}T00:00:00`);
  const wochentag = (lang ? WOCHENTAGE_LANG : WOCHENTAGE_KURZ)[d.getDay()];
  const tt = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return lang ? `${wochentag}, ${tt}.${mm}.` : `${wochentag} ${tt}.${mm}.`;
}

function Tagesablauf() {
  const [tage, setTage] = useState([]);
  const [ausgewaehlterTag, setAusgewaehlterTag] = useState(null);
  const [zeilen, setZeilen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jetzt, setJetzt] = useState(new Date());

  // Verfügbare Tage laden, standardmäßig heute auswählen (falls vorhanden)
  useEffect(() => {
    fetch(`${host_url}/tagesablauf/tage`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const geladeneTage = Array.isArray(data) ? data : [];
        setTage(geladeneTage);
        const heute = heutigesDatum();
        setAusgewaehlterTag(geladeneTage.includes(heute) ? heute : geladeneTage[0] || null);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Fehler beim Laden der Tagesablauf-Tage:', err);
        setLoading(false);
      });
  }, []);

  // Zeilen + Slots des gewählten Tages laden
  useEffect(() => {
    if (!ausgewaehlterTag) return;
    fetch(`${host_url}/tagesablauf?datum=${ausgewaehlterTag}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => setZeilen(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error('Fehler beim Laden des Tagesablaufs:', err);
        setZeilen([]);
      });
  }, [ausgewaehlterTag]);

  // Roter "Jetzt"-Balken: jede Minute aktualisieren
  useEffect(() => {
    const interval = setInterval(() => setJetzt(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  const istHeute = ausgewaehlterTag === heutigesDatum();
  const jetztMinuten = jetzt.getHours() * 60 + jetzt.getMinutes() - START_STUNDE * 60;
  const zeigeJetztBalken = istHeute && jetztMinuten >= 0 && jetztMinuten <= GESAMT_MINUTEN;
  const jetztProzent = (jetztMinuten / GESAMT_MINUTEN) * 100;
  const jetztUhrzeit = `${String(jetzt.getHours()).padStart(2, '0')}:${String(jetzt.getMinutes()).padStart(2, '0')}`;

  return (
    <div className="tagesablauf-container">
      <h1>Tagesablauf</h1>

      {loading ? (
        <div className="tagesablauf-empty">
          <p>Laden…</p>
        </div>
      ) : tage.length === 0 ? (
        <div className="tagesablauf-empty">
          <span className="material-symbols-outlined">schedule</span>
          <p>Es ist noch kein Tagesablauf hinterlegt.</p>
        </div>
      ) : (
        <>
          {/* Tages-Tabs */}
          <div className="tagesablauf-tabs">
            {tage.map((datum) => (
              <button
                key={datum}
                className={`tagesablauf-tab ${ausgewaehlterTag === datum ? 'active' : ''}`}
                onClick={() => setAusgewaehlterTag(datum)}
              >
                {tagLabel(datum)}
              </button>
            ))}
          </div>

          <h2 className="tagesablauf-tagestitel">{ausgewaehlterTag && tagLabel(ausgewaehlterTag, true)}</h2>

          {zeigeJetztBalken && (
            <div className="tagesablauf-jetzt-hinweis">
              <span className="jetzt-punkt" /> Jetzt: {jetztUhrzeit} Uhr
            </div>
          )}

          {/* Gantt-Tabelle */}
          <div className="ta-chart-scroll">
            <div className="ta-chart">
              {/* Kopfzeile mit Stunden */}
              <div className="ta-row ta-header-row">
                <div className="ta-row-label ta-header-label">Uhrzeit</div>
                <div className="ta-row-track ta-header-track">
                  {STUNDEN.map((stunde) => (
                    <span
                      key={stunde}
                      className="ta-stunde"
                      style={{ left: `${((stunde - START_STUNDE) * 60 / GESAMT_MINUTEN) * 100}%` }}
                    >
                      {stunde}
                    </span>
                  ))}
                </div>
              </div>

              {/* Aktivitäten-Zeilen; der rote Balken liegt über allen Zeilen */}
              <div className="ta-body">
                {zeilen.map((zeile) => (
                  <div className="ta-row" key={zeile.ID}>
                    <div className="ta-row-label">{zeile.Name}</div>
                    <div className="ta-row-track">
                      {zeile.Slots.map((slot) => {
                        const links = zeitZuProzent(slot.Start);
                        const breite = Math.max(zeitZuProzent(slot.Ende) - links, 0.5);
                        return (
                          <div
                            key={slot.ID}
                            className="ta-slot"
                            style={{ left: `${links}%`, width: `${breite}%`, backgroundColor: slot.Farbe }}
                            title={`${slot.Start}–${slot.Ende}${slot.Label ? ` ${slot.Label}` : ''}`}
                          >
                            {slot.Label && <span className="ta-slot-label">{slot.Label}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {zeigeJetztBalken && (
                  <div className="ta-jetzt-overlay">
                    <div className="ta-jetzt-balken" style={{ left: `${jetztProzent}%` }} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Tagesablauf;
